import React, { useState, useEffect, useRef } from 'react';
import {
  Shield,
  Upload,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Ban,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from '@/i18n';
import { apiClient, ExtensionCertificate } from '@/utils/apiClient';

export const ExtensionCertificates: React.FC = () => {
  const { t } = useTranslation();

  const [certs, setCerts] = useState<ExtensionCertificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadCerts();
  }, []);

  const loadCerts = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.listExtensionCertificates();
      setCerts(res.certificates);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load extension certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    try {
      await apiClient.uploadExtensionCertificate(file);
      await loadCerts();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to upload extension certificate');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRevoke = async (certId: string) => {
    setRevoking(certId);
    setError(null);
    try {
      await apiClient.revokeExtensionCertificate(certId);
      setConfirmRevoke(null);
      await loadCerts();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to revoke certificate');
    } finally {
      setRevoking(null);
    }
  };

  const getStatusBadge = (cert: ExtensionCertificate) => {
    switch (cert.status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
            <CheckCircle className="h-3 w-3 mr-1" />
            {t.settings.certActive || 'In force'}
          </span>
        );
      case 'inactive':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
            <XCircle className="h-3 w-3 mr-1" />
            {t.settings.certInactive || 'Not in force'}
          </span>
        );
      case 'unknown':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
            <AlertTriangle className="h-3 w-3 mr-1" />
            {t.settings.certStatusUnknown || 'MCP status unavailable'}
          </span>
        );
      case 'revoked':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400">
            <Ban className="h-3 w-3 mr-1" />
            {t.settings.certRevoked || 'Revoked'}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
            <AlertTriangle className="h-3 w-3 mr-1" />
            {t.settings.certError || 'Error'}
          </span>
        );
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <Shield className="h-5 w-5 text-indigo-500" />
          <div className="text-left">
            <span className="font-medium text-gray-900 dark:text-white">
              {t.settings.extensionCertificates || 'Extension Certificates'}
            </span>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t.settings.extensionCertDescription || 'Manage extension certificates that extend API limits or validity periods'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {certs.length > 0 && (
            <span className="text-xs text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
              {certs.length}
            </span>
          )}
          {expanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4">
          {/* Error */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 text-sm text-red-700 dark:text-red-300 flex items-center">
              <XCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              {error}
              <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
                <XCircle className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {/* Upload button */}
          <div className="flex items-center space-x-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pem"
              onChange={handleUpload}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-md transition-colors"
            >
              {uploading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              {t.settings.uploadExtensionCert || 'Upload Extension Certificate'}
            </button>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
            </div>
          )}

          {/* Certificate list */}
          {!loading && certs.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
              {t.settings.noExtensionCerts || 'No extension certificates installed'}
            </p>
          )}

          {!loading && certs.length > 0 && (
            <div className="space-y-3">
              {certs.map((cert) => (
                <div
                  key={cert.cert_id}
                  className={clsx(
                    'border rounded-lg p-3',
                    cert.status === 'revoked'
                      ? 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 opacity-60'
                      : cert.status === 'active'
                      ? 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10'
                      : 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10'
                  )}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(cert)}
                      <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                        {cert.cert_id.substring(0, 12)}...
                      </span>
                    </div>
                    {cert.status !== 'revoked' && (
                      <>
                        {confirmRevoke === cert.cert_id ? (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-red-600 dark:text-red-400">
                              {t.settings.confirmRevoke || 'Confirm revoke?'}
                            </span>
                            <button
                              onClick={() => handleRevoke(cert.cert_id)}
                              disabled={revoking === cert.cert_id}
                              className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded transition-colors"
                            >
                              {revoking === cert.cert_id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                'Yes'
                              )}
                            </button>
                            <button
                              onClick={() => setConfirmRevoke(null)}
                              className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded transition-colors"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmRevoke(cert.cert_id)}
                            className="flex items-center px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          >
                            <Ban className="h-3 w-3 mr-1" />
                            {t.settings.revokeExtensionCert || 'Revoke'}
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
                    {cert.extension_type && (
                      <div>
                        <span className="text-gray-500">{t.settings.extensionType || 'Type'}:</span>{' '}
                        <span className="font-medium">{cert.extension_type}</span>
                      </div>
                    )}
                    {cert.new_api_limit != null && (
                      <div>
                        <span className="text-gray-500">{t.settings.apiLimit || 'API Limit'}:</span>{' '}
                        <span className="font-medium">{cert.new_api_limit.toLocaleString()}</span>
                      </div>
                    )}
                    {cert.extension_days != null && (
                      <div>
                        <span className="text-gray-500">{t.settings.extensionDays || 'Extension Days'}:</span>{' '}
                        <span className="font-medium">{cert.extension_days}</span>
                      </div>
                    )}
                    {cert.days_remaining != null && cert.status !== 'revoked' && (
                      <div>
                        <span className="text-gray-500">{t.settings.daysRemaining || 'days remaining'}:</span>{' '}
                        <span className={clsx(
                          'font-medium',
                          cert.days_remaining < 7 ? 'text-red-500' :
                          cert.days_remaining < 30 ? 'text-yellow-500' : 'text-green-500'
                        )}>
                          {cert.days_remaining}
                        </span>
                      </div>
                    )}
                    {cert.valid_from && (
                      <div>
                        <span className="text-gray-500">{t.settings.certValidFrom || 'Valid From'}:</span>{' '}
                        {new Date(cert.valid_from).toLocaleDateString()}
                      </div>
                    )}
                    {cert.valid_until && (
                      <div>
                        <span className="text-gray-500">{t.settings.certValidUntil || 'Valid Until'}:</span>{' '}
                        {new Date(cert.valid_until).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
